
import { defineComponent } from 'vue';
import NearbyTrailerResult from '@/types/NearbyTrailerResult';
import { NearbyTrailerResultType } from '@/types/NearbyTrailerResultType';
import { isNullOrUndefined } from '@/shared/helpers';
import { DateTime } from 'luxon';
import { CargoStatus } from '@/types/CargoStatus';

export default defineComponent({
    name: 'LocationContent',
    components: {},
    emits: ['viewAvailableTrailers'],
    props: {
        displayedResult: {
            type: NearbyTrailerResult,
            required: true,
        },
        locationTitle: {
            type: String,
            required: true,
        },
        travelTime: {
            type: String,
            required: true,
        },
    },
    computed: {
        isCustomerLocation(): boolean {
            return (
                this.displayedResult?.type ===
                NearbyTrailerResultType.customerLocation
            );
        },
        isReserved(): boolean {
            return (
                this.displayedResult.trailers.findIndex(
                    (item) => item?.reservation?.isActive
                ) > -1
            );
        },
        locationAddressLineOne(): string {
            if (this.isCustomerLocation) {
                return (
                    this.displayedResult.locationAddress?.label ?? ''
                ).toLowerCase();
            } else {
                const city = this.displayedResult.locationAddress?.city;
                const addressSplit = this.displayedResult.locationAddress?.label
                    .split(city ?? '')[0]
                    .trim();
                return (
                    addressSplit?.substring(0, addressSplit.length - 1) ?? ''
                );
            }
        },
        hasAnyFault() {
            return (
                this.hasDotExpired === true || this.hasCargoSensorError === true
            );
        },
        hasDotExpired(): boolean {
            return (
                this.displayedResult?.trailers[0].isDotInspectionExpired ===
                true
            );
        },
        hasCargoSensorError(): boolean {
            return (
                this.displayedResult?.trailers[0].cargoStatus ===
                    CargoStatus.SENSOR_ERROR ||
                this.displayedResult?.trailers[0].cargoStatus ===
                    CargoStatus.NO_SENSOR
            );
        },
        hasCargoSensor(): boolean {
            return (
                this.displayedResult?.trailers[0].cargoStatus !==
                CargoStatus.NO_SENSOR
            );
        },
        hasExclusivelyDotExpiredTrailers() {
            let hasDotExpiredTrailers = true;

            this.displayedResult.trailers.forEach((trailer) => {
                if (trailer.isDotInspectionExpired === false) {
                    hasDotExpiredTrailers = false;
                }
            });

            return hasDotExpiredTrailers;
        },
        lastUpdated(): string | undefined {
            if (isNullOrUndefined(this.displayedResult))
                return 'Last update unknown';

            const age = this.displayedResult?.trailers[0].sourceLastReported;
            if (age === null || age === undefined) return 'Last update unknown';
            else {
                const timestamp = age.toRelative({
                    unit: ['days', 'hours', 'minutes'],
                });
                const value = timestamp
                    ?.split(' ')
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ');
                return `Updated ${value}`;
            }
        },
        locationAddressLineTwo(): string {
            if (this.isCustomerLocation) {
                let addressLineTwo =
                    this.displayedResult?.locationAddress?.city?.toLowerCase() ??
                    '';
                addressLineTwo += addressLineTwo.length > 0 ? ', ' : '';
                addressLineTwo +=
                    this.displayedResult.locationAddress?.state ?? '';
                addressLineTwo +=
                    this.displayedResult.locationAddress?.state === undefined
                        ? ''
                        : ' ';
                addressLineTwo +=
                    this.displayedResult.locationAddress?.zip ?? '';
                return addressLineTwo;
            } else {
                const city = this.displayedResult.locationAddress?.city;
                if (isNullOrUndefined(city)) {
                    return this.displayedResult.locationAddress?.label ?? '';
                } else {
                    return `${city ?? ''}${
                        this.displayedResult.locationAddress?.label?.split(
                            city ?? ''
                        )[1] ?? ''
                    }`;
                }
            }
        },
        distance(): string {
            return this.displayedResult.distance.toFixed(1);
        },
        arrivalTime(): string {
            if (this.travelTime === '?') {
                return this.travelTime;
            }
            const arrivalDate = DateTime.now().plus({
                minutes: parseInt(this.travelTime),
            });
            return `${arrivalDate.hour}:${arrivalDate.minute < 10 ? '0' : ''}${
                arrivalDate.minute
            }`;
        },
        totalTrailerText(): string {
            const totalTrailers = this.isCustomerLocation
                ? this.displayedResult.trailers.length
                : 1;
            return totalTrailers.toString();
        },
        trailerNumber(): string {
            return this.displayedResult?.trailers[0]?.trailerNumber || '';
        },
        trailerCompany(): string {
            return this.displayedResult?.trailers[0]?.company || '';
        },
    },
    methods: {
        onViewTrailers() {
            this.$emit('viewAvailableTrailers', true);
        },
    },
});
